import { Suspense, useCallback, useContext } from 'react';
import { Customer, Dimensions, ExtraBase, SmartSendItemApiModel } from '~/lib/api/smartsend/types';
import { SmartSendContext } from '../../../lib/context/SmartSendContext';
import { SaveItemsAction } from '../../../lib/context/SmartSendContextModels';
import { ApplierOption } from '../../../lib/helpers/ApplierOption';
import GetTariffSystemFromItem, { GetTariffSystemLengthFromItem } from '../../../lib/helpers/GetTariffSystemFromItem';
import { getLocalUnitsEntry, reconcileLocalUnitsEntry } from '~/apps/smartsend/lib/helpers/LocalUnitsEntryHelper';
import { localUnitsEntryHasValues, LocalUnitsEntry } from '~/apps/smartsend/lib/helpers/DimensionUnits';
import { TariffSuggestions } from '../../customs/TariffSuggestions';
import { DimensionsSelection } from '../DimensionsSelection';
import { P2GBox, P2GText } from '@parcel2go/core-ui';
import UnavailableExtras from './UnavailableExtras';
import QuickFixCounty from './QuickFixCounty';

interface props {
  item: SmartSendItemApiModel;
  error: string;
  children: React.ReactNode;
}

const QuickFix: React.FC<props> = (props) => {
  const { dispatch } = useContext(SmartSendContext);

  const onDimensionsSave = useCallback(
    (dims: Dimensions, localUnitsEntry?: LocalUnitsEntry) => {
      const actions: ApplierOption[] = ['Dimensions'];
      const changes: { Dimensions: Dimensions; Meta?: SmartSendItemApiModel['Meta'] } = { Dimensions: dims };
      // Only touch Meta if there's something to write or an existing record to clear - a metric
      // entry with no as-entered history and no existing record must not stamp a blank one.
      if (localUnitsEntryHasValues(localUnitsEntry) || getLocalUnitsEntry(props.item.Meta)) {
        const meta = { ...props.item.Meta };
        reconcileLocalUnitsEntry(meta, localUnitsEntry);
        changes.Meta = meta;
        actions.push('Meta');
      }
      dispatch(new SaveItemsAction([props.item.Id], actions, changes));
    },
    [dispatch, props.item.Id, props.item.Meta]
  );

  const onUnavailableExtrasRemoval = useCallback(
    (extras: ExtraBase[]) => {
      dispatch(
        new SaveItemsAction([props.item.Id], ['Extras'], {
          Extras: extras
        })
      );
    },
    [dispatch, props.item]
  );

  const onRecipientSave = useCallback(
    (recipient: Customer) => {
      dispatch(
        new SaveItemsAction([props.item.Id], ['Recipient'], {
          Recipient: recipient
        })
      );
    },
    [dispatch, props.item]
  );

  const onSenderSave = useCallback(
    (sender: Customer) => {
      dispatch(
        new SaveItemsAction([props.item.Id], ['Sender'], {
          Sender: sender
        })
      );
    },
    [dispatch, props.item]
  );

  // const setDate = async (d: Date) =>
  //   dispatch(new SaveItemsAction([props.item.Id], ['PreferredCollectionDate'], { PreferredCollectionDate: d }));

  const onTariffCodeSave = useCallback(
    (id: string, code: string) => {
      const ols = [...props.item.MarketplaceOrderLines];
      ols.forEach((o) => o.Id === id && (o.Product.TariffCode = code));
      dispatch(
        new SaveItemsAction([props.item.Id], ['MarketplaceOrderLines'], {
          MarketplaceOrderLines: ols
        })
      );
    },
    [dispatch, props.item.Id, props.item.MarketplaceOrderLines]
  );

  switch (props.error) {
    case 'ADDRESS-RECIPIENT-COUNTY-INVALID-OPTION':
      return (
        <P2GBox display="flex" flexDirection="column" gap={2}>
          <Suspense>
            <QuickFixCounty customer={props.item.Recipient} onSave={onRecipientSave} fallback={props.children} />
          </Suspense>
        </P2GBox>
      );

    case 'ADDRESS-SENDER-COUNTY-INVALID-OPTION':
      return (
        <P2GBox display="flex" flexDirection="column" gap={2}>
          <Suspense>
            <QuickFixCounty customer={props.item.Sender} onSave={onSenderSave} fallback={props.children} />
          </Suspense>
        </P2GBox>
      );

    case 'PRODUCT-TARIFFCODE-TOO-SHORT':
    case 'PRODUCT-TARIFFCODE-REQUIRED':
      return (
        <P2GBox display="flex" flexDirection="column" gap={2}>
          {props.children}
          <P2GText variant="caption">
            Your Tariff Code should be {GetTariffSystemLengthFromItem(props.item)} digits long.
          </P2GText>
          <Suspense>
            {props.item.MarketplaceOrderLines.map((m) => (
              <TariffSuggestions
                key={m.Id}
                title={`Suggestion for '${m.Product.Description}':`}
                canSelect={true}
                limit={1}
                selectCode={(v) => onTariffCodeSave(m.Id, v)}
                tariffSystem={GetTariffSystemFromItem(props.item)}
                itemDescription={m.Product.Description}
              />
            ))}
          </Suspense>
        </P2GBox>
      );

    case 'ITEM-WEIGHT':
      return (
        <DimensionsSelection
          dimensions={props.item.PackageDimensions}
          localUnitsEntry={getLocalUnitsEntry(props.item.Meta)}
          onSave={onDimensionsSave}
          startMinimised={false}
          dimensionsRequired={false}
          hideDimensionsIfNotRequired={true}
          showWhenTouched={true}
        />
      );

    case 'EXTRAS-UNAVAILABLE':
      return <UnavailableExtras item={props.item} onRemove={onUnavailableExtrasRemoval} />;

    default:
      return <>{props.children}</>;
  }
};

export default QuickFix;
